// The client header's facts, merged from the two places they may live.
//
// fn_client_evidence (compact identity block) and fn_client_context.client both
// describe the client, and neither shape is confirmed live — evidence has been
// reported as permission-denied, and context.client is only assumed from what
// ClientSlideOut reads. So the header takes each field from evidence when present
// and falls back to context.client, and omits what neither carries. Nothing is
// ever guessed or defaulted.

/** First non-empty value, or null. */
const firstPresent = (...values) =>
  values.find((v) => v != null && !(typeof v === 'string' && v.trim() === '')) ?? null;

/** Whether a contact row carries anything to show; a row of nulls is no contact. */
const hasAnyField = (c) => firstPresent(c?.name, c?.phone, c?.email) !== null;

/** Contacts from the first source that has any real one (empty rows do not count). */
const contactsOf = (evidence, client) => {
  const pick = (list) => (Array.isArray(list) ? list.filter(hasAnyField) : []);
  const fromEvidence = pick(evidence?.contacts);
  return fromEvidence.length > 0 ? fromEvidence : pick(client?.contacts);
};

/** A service may be a string or an object carrying name / service. */
const serviceName = (s) => (typeof s === 'string' ? s : s?.name || s?.service || null);

/** Services from the first source that has any, as display strings. */
const servicesOf = (evidence, client) => {
  const pick = (list) => (Array.isArray(list) ? list.map(serviceName).filter(Boolean) : []);
  const fromEvidence = pick(evidence?.active_services);
  return fromEvidence.length > 0 ? fromEvidence : pick(client?.active_services);
};

/**
 * A value as an absolute http(s) address, or null. Anything else (a relative path, a
 * `javascript:` or `data:` value, a non-string) is dropped rather than put in an image source.
 *
 * @param {*} value
 * @returns {string|null}
 */
export function webUrlOf(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return /^https?:\/\/\S+$/i.test(trimmed) ? trimmed : null;
}

/**
 * Merge evidence and context.client into the header's facts.
 *
 * The PRIMARY contact is only the one flagged `is_primary`; when nothing is
 * flagged the first contact is shown under the plain label "Contact" rather than
 * being promoted to primary on a guess. When neither source has ANY contact, the
 * suggested Write-to person stands in under that same plain label (and
 * `contactSuggested` says so, so the header can explain it is not a primary contact).
 *
 * @param {object} sources
 * @param {object|null} [sources.evidence]     fn_client_evidence payload
 * @param {object|null} [sources.client]       fn_client_context.client
 * @param {string|null} [sources.rosterName]   Name from fn_get_my_clients (fallback only)
 * @param {{name?:string|null, email?:string|null}|null} [sources.fallbackContact]
 *   The suggested Write-to person (see suggestedContact); used ONLY when neither source has a contact.
 * `industry`, `timezone` and `subscription` are read from whichever source carries them
 * (none is confirmed to today), so the header can show a real value the day one arrives
 * and a placeholder until then.
 *
 * `logoUrl` is the client's logo as an absolute web address, or null (display only; nothing edits it here).
 *
 * @returns {{name:string|null, location:string|null, website:string|null, logoUrl:string|null,
 *   contact:object|null, contactLabel:string, contactSuggested:boolean, phone:string|null, email:string|null,
 *   services:string[], industry:string|null, timezone:string|null,
 *   subscription:string|null}}
 */
export function mergeClientFacts({ evidence = null, client = null, rosterName = null, fallbackContact = null } = {}) {
  const city = firstPresent(evidence?.city, client?.city);
  const state = firstPresent(evidence?.state, client?.state);
  const contacts = contactsOf(evidence, client);
  const primary = contacts.find((c) => c?.is_primary === true) || null;
  // The fallback only fills a header with no contact at all; any real contact outranks it.
  const suggested = contacts.length === 0 && (fallbackContact?.name || fallbackContact?.email) ? fallbackContact : null;
  const contact = primary || contacts[0] || suggested;

  return {
    name: firstPresent(evidence?.name, client?.name, rosterName),
    location: [city, state].filter(Boolean).join(', ') || null,
    website: firstPresent(evidence?.website, client?.website),
    logoUrl: webUrlOf(firstPresent(evidence?.logo_url, client?.logo_url)),
    contact,
    contactLabel: primary ? 'Primary contact' : 'Contact',
    contactSuggested: contact === suggested && suggested !== null,
    phone: firstPresent(contact?.phone),
    email: firstPresent(contact?.email),
    services: servicesOf(evidence, client),
    industry: firstPresent(evidence?.industry, client?.industry),
    timezone: firstPresent(evidence?.timezone, client?.timezone),
    subscription: firstPresent(evidence?.subscription_status, client?.subscription_status),
  };
}

/**
 * A timezone id as a friendly name: "America/Denver" reads "Mountain Time". Anything
 * the engine cannot name (an id it rejects, an already-friendly string such as
 * "Central time", an engine without the generic name) comes back unchanged, never guessed.
 *
 * @param {*} id  An IANA timezone id, normally a string
 * @returns {string|null}  The friendly name, the raw value when it cannot be named, or null when absent
 */
export function friendlyTimezone(id) {
  if (id == null || (typeof id === 'string' && id.trim() === '')) return null;
  if (typeof id !== 'string') return String(id);
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: id, timeZoneName: 'longGeneric' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName');
    return part?.value || id;
  } catch {
    return id;
  }
}

/**
 * The Elsewhere links with the client's website first, so the site stays reachable
 * without a header button. Accepts both shapes ElsewhereLinks does (array of
 * { label, url } or an object keyed by destination); an existing "website" entry wins.
 *
 * @param {Array<{label?:string,url?:string}>|Record<string,*>|null|undefined} elsewhere
 * @param {string|null} website
 * @returns {Array<{label?:string,url?:string}>|Record<string,*>|null|undefined}
 */
export function withWebsite(elsewhere, website) {
  if (!website) return elsewhere;
  if (Array.isArray(elsewhere)) {
    const has = elsewhere.some((x) => (x?.url || x?.href) === website);
    return has ? elsewhere : [{ label: 'Website', url: website }, ...elsewhere];
  }
  const obj = elsewhere && typeof elsewhere === 'object' ? elsewhere : {};
  return Object.prototype.hasOwnProperty.call(obj, 'website') ? obj : { website, ...obj };
}

/**
 * Up-to-two-letter initials for the avatar tile, from the first two words.
 * @param {string|null} name
 * @returns {string}
 */
export function initialsOf(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  return words.slice(0, 2).map((w) => w[0].toUpperCase()).join('');
}
